import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { hideError, onAnimationEnd, prefersReducedMotion, showError } from '@theme/utilities';

/** Where the bar goes full-width and can reach the widgets; matches product-information.liquid. */
const STICKY_BAR_FULL_WIDTH = '(max-width: 989px)';

/** Matches the product form's own error slot. */
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/** Flags the body while the bar is showing, so vendors.css can lift the app widgets. */
const WIDGETS_LIFTED_CLASS = 'widgets-lifted';

/** How far they move, published on the root; see vendors.css. */
const LIFT_PROPERTY = '--sticky-bar-lift';

/** Breathing room left between a lifted widget and the bar, in px. */
const WIDGET_GAP = 12;

/** Breathing room left between a lifted widget and the bar, in px. */
const FLOATING_WIDGET_GAP = 12;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} [errorText] - The bar's own error slot
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** Set between this bar's click and the cart's answer, so it ignores everyone else's adds. */
  #awaitingAdd = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Rotating the device changes both boxes, so the lift has to be measured again.
    window.addEventListener('resize', this.#syncFloatingWidgets, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    document.body.classList.remove(WIDGETS_LIFTED_CLASS);
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      },
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Nothing gets added without a chosen option, and the success animation below would claim
    // otherwise (SHINE-887). Raise the form's own "Please select size" error and send the shopper
    // to the picker, rather than puppeting a click whose validation never reaches them.
    const productForm = this.#getProductForm();

    if (productForm && !productForm.hasSelectedVariant) {
      productForm.showMissingOptionError();
      this.#showError(this.#getFormErrorMessage());
      this.#scrollToVariantPicker();
      return;
    }

    this.#hideError();
    this.#awaitingAdd = true;
    this.refs.addToCartButton.dataset.loading = 'true';
    this.refs.addToCartButton.setAttribute('aria-busy', 'true');

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Marks the add on the bar's own button, once the cart has answered.
   */
  async #animateAdded() {
    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd(this.refs.addToCartButton);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Reads whatever the product form just put in its own error slot.
   *
   * The form builds these messages — naming the missing option, or reading the cart's
   * response — so the bar echoes that rather than composing its own.
   *
   * @returns {string}
   */
  #getFormErrorMessage() {
    const slot = this.#getProductForm()?.refs?.addToCartTextError;
    return slot?.textContent?.trim() ?? '';
  }

  /**
   * Shows a message in the bar's own error slot, shaking it if one is already up.
   *
   * @param {string} message
   */
  #showError(message) {
    const { errorText } = this.refs;
    if (!errorText || !message) return;

    showError(errorText, message);

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /** Clears the bar's error slot. */
  #hideError() {
    const { errorText } = this.refs;
    if (!errorText) return;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    hideError(errorText);
  }

  #scrollToVariantPicker() {
    const scope = this.#getProductForm()?.closest('.product-details');
    const pickers = /** @type {HTMLElement[]} */ ([...(scope?.querySelectorAll('variant-picker') ?? [])]);

    // A picker block can be set hidden, and scrolling to something with no box goes nowhere.
    const picker = pickers.find((element) => element.offsetParent !== null);
    if (!picker) return;

    picker.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });

    const firstOption = picker.querySelector('fieldset input[data-option-value-id]:not([disabled])');
    if (firstOption instanceof HTMLElement) firstOption.focus({ preventScroll: true });
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input[data-option-value-id]:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (!this.#awaitingAdd) return;
    this.#awaitingAdd = false;

    this.refs.addToCartButton.removeAttribute('data-loading');
    this.refs.addToCartButton.removeAttribute('aria-busy');

    const { detail } = /** @type {CustomEvent} */ (event);

    if (detail?.data?.didError || detail?.data?.message) {
      this.#showError(detail.data.message || this.#getFormErrorMessage());
      return;
    }

    this.#animateAdded();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncFloatingWidgets();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#syncFloatingWidgets();
  }

  /** @type {number | null} */
  #syncFrame = null;

  #syncFloatingWidgets = () => {
    // Resize fires in bursts while a window is dragged; one measure per frame is enough.
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(this.#measureFloatingWidgets);
  };

  /**
   * Flags the body while a sticky bar is showing and publishes how far the floating app
   * widgets have to move to clear it. Nothing is written to the widgets themselves —
   * their apps re-render them and rewrite their style attributes, so vendors.css matches
   * whichever element currently exists instead.
   */
  #measureFloatingWidgets = () => {
    this.#syncFrame = null;

    const stuckBar = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');

    // Above the breakpoint the bar is a centred pill that never reaches these widgets.
    if (!stuckBar || !window.matchMedia(STICKY_BAR_FULL_WIDTH).matches) {
      document.body.classList.remove(WIDGETS_LIFTED_CLASS);
      return;
    }

    // Derived from the bar rather than measured against each widget, so a widget that
    // mounts later is already in the right place the moment it appears.
    const { height } = stuckBar.getBoundingClientRect();

    document.documentElement.style.setProperty(LIFT_PROPERTY, `${Math.ceil(height + WIDGET_GAP)}px`);
    document.body.classList.add(WIDGETS_LIFTED_CLASS);
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
